import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sidebar } from '@/components/Sidebar';
import { Upload, FileUp, Users, CalendarDays, Wallet, Check, Loader2, ArrowRight, X, Search } from 'lucide-react';
import { formatMoney } from '@/lib/currency';

const ALLOWED = '.xlsx,.docx,.txt,.csv';

export default function ImportPage() {
  const [files, setFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(null);
  const [clients, setClients] = useState([]);
  const [targetClient, setTargetClient] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [currency, setCurrency] = useState('RUB');
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get('/patients'), api.get('/settings')])
      .then(([{ data: patients }, { data: settings }]) => {
        setClients(patients);
        setCurrency(settings?.currency || 'RUB');
      })
      .catch(() => {});
  }, []);

  const analyze = async () => {
    if (!files.length) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      fd.append('target_client', targetClient);
      const { data } = await api.post('/import/analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.errors?.length) data.errors.forEach(e => toast.error(e));
      setPreview(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка анализа'); }
    setAnalyzing(false);
  };

  const confirmImport = async () => {
    if (!preview) return;
    const unresolved = (preview.appointments || []).filter(a => !a.format_id).length;
    if (unresolved > 0) {
      toast.error(`Выберите формат для ${unresolved} записей`);
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post('/import/confirm', preview);
      setDone(data.created);
      toast.success('Данные импортированы!');
      if (data.created?.columns_skipped) {
        toast.warning(`Не добавлено столбцов: ${data.created.columns_skipped}. Лимит — 24`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка импорта'); }
    setImporting(false);
  };

  const reset = () => { setFiles([]); setPreview(null); setDone(null); setTargetClient(''); };

  const handleDrop = (e) => { e.preventDefault(); if (e.dataTransfer.files.length) setFiles([...e.dataTransfer.files]); };

  const filteredClients = clients.filter(c => !clientSearch || c.name?.toLowerCase().includes(clientSearch.toLowerCase()));

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-auto">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8 animate-fade-in">
            <h1 className="font-heading text-2xl lg:text-3xl" style={{ color: 'var(--app-text)' }}>Импорт данных</h1>
            <p className="text-sm mt-2" style={{ color: 'var(--app-text-muted)' }}>Загрузите файлы — ИИ распознает клиентов, записи и расходы</p>
          </div>

          {/* Privacy notice */}
          <div className="mb-6 p-4 rounded-xl border animate-fade-in" style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--app-accent-muted)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--app-accent)' }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <div>
                <p className="text-sm font-medium mb-1" style={{ color: 'var(--app-text)' }}>Безопасность ваших данных</p>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--app-text-muted)' }}>
                  Мы не читаем и не передаём ваши файлы за рубеж. Анализ выполняет YandexGPT — российская ИИ-модель, работающая на серверах Яндекса в РФ. Содержимое файлов используется исключительно для распознавания структуры данных и не сохраняется после обработки. Загружая файлы, вы даёте согласие на их обработку с помощью ИИ.
                </p>
              </div>
            </div>
          </div>

          {/* Done */}
          {done && (
            <Card className="animate-fade-in" style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--app-accent-muted)' }}>
                  <Check size={32} style={{ color: 'var(--app-accent)' }} />
                </div>
                <h2 className="font-heading text-xl mb-4" style={{ color: 'var(--app-text)' }}>Импорт завершён</h2>
                <div className="flex justify-center gap-6 mb-6">
                  <Stat icon={Users} label="Клиентов" value={done.clients} />
                  <Stat icon={CalendarDays} label="Записей" value={done.appointments} />
                  <Stat icon={Wallet} label="Расходов" value={done.expenses} />
                </div>
                <div className="flex justify-center gap-3">
                  <Button onClick={reset} variant="outline" style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-secondary)' }}>Ещё</Button>
                  <Button onClick={() => navigate('/patients')} className="text-white" style={{ background: 'var(--app-accent)' }}>
                    К клиентам <ArrowRight size={14} className="ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upload */}
          {!done && !preview && (
            <div className="space-y-4 animate-fade-in">
              {/* Target client selector */}
              <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <CardContent className="p-4">
                  <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--app-text-secondary)' }}>
                    Для кого эти данные? <span className="font-normal" style={{ color: 'var(--app-text-faint)' }}>(необязательно)</span>
                  </label>
                  {targetClient ? (
                    <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'var(--app-accent-muted)' }}>
                      <Users size={14} style={{ color: 'var(--app-accent)' }} />
                      <span className="text-sm flex-1" style={{ color: 'var(--app-text)' }}>{targetClient}</span>
                      <button onClick={() => setTargetClient('')} style={{ color: 'var(--app-text-muted)' }}><X size={14} /></button>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--app-text-faint)' }} />
                        <Input
                          value={clientSearch}
                          onChange={e => { setClientSearch(e.target.value); setShowClientPicker(true); }}
                          onFocus={() => setShowClientPicker(true)}
                          placeholder="Выберите клиента или оставьте пустым"
                          className="pl-9 text-sm" style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} />
                      </div>
                      {showClientPicker && filteredClients.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 rounded-lg border max-h-40 overflow-y-auto"
                          style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                          {filteredClients.slice(0, 10).map(c => (
                            <button key={c.id} className="w-full text-left px-3 py-2 text-sm hover:bg-stone-800 transition-colors"
                              style={{ color: 'var(--app-text)' }}
                              onClick={() => { setTargetClient(c.name); setShowClientPicker(false); setClientSearch(''); }}>
                              {c.name} <span className="text-xs" style={{ color: 'var(--app-text-faint)' }}>{c.phone}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* File drop */}
              <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <CardContent className="p-6">
                  <label className="cursor-pointer block"
                    onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
                    <input type="file" className="hidden" accept={ALLOWED} multiple
                      onChange={e => setFiles(e.target.files ? [...e.target.files] : [])} />
                    <div className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors`}
                      style={{ borderColor: files.length ? 'var(--app-accent)' : 'var(--app-border)' }}>
                      {files.length > 0 ? (
                        <div>
                          <FileUp size={36} className="mx-auto mb-3" style={{ color: 'var(--app-accent)' }} />
                          <p className="text-sm font-medium" style={{ color: 'var(--app-text)' }}>{files.length} файл(ов)</p>
                          <div className="flex flex-wrap justify-center gap-2 mt-2">
                            {files.map((f, i) => (
                              <Badge key={i} className="text-xs" style={{ background: 'var(--app-card-deep)', color: 'var(--app-text-secondary)' }}>
                                {f.name} ({(f.size / 1024).toFixed(0)} КБ)
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Upload size={36} className="mx-auto mb-3" style={{ color: 'var(--app-text-faint)' }} />
                          <p className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>Нажмите или перетащите файлы</p>
                          <p className="text-xs mt-1" style={{ color: 'var(--app-text-faint)' }}>xlsx, docx, txt, csv · можно несколько</p>
                        </div>
                      )}
                    </div>
                  </label>
                  {files.length > 0 && (
                    <div className="flex gap-2 mt-4">
                      <Button onClick={() => setFiles([])} variant="outline" className="flex-1" style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-secondary)' }}>Сброс</Button>
                      <Button onClick={analyze} disabled={analyzing} className="flex-1 text-white" style={{ background: 'var(--app-accent)' }}>
                        {analyzing ? <><Loader2 size={14} className="mr-2 animate-spin" /> Анализ ({files.length} файл.)...</> : `Анализировать (${files.length})`}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Preview */}
          {preview && !done && (
            <div className="space-y-4 animate-fade-in">
              <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-heading text-base" style={{ color: 'var(--app-text)' }}>Распознано из {preview.files_processed} файл(ов)</h3>
                      {targetClient && <p className="text-xs mt-0.5" style={{ color: 'var(--app-accent)' }}>Для клиента: {targetClient}</p>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={reset} style={{ color: 'var(--app-text-muted)' }}><X size={16} /></Button>
                  </div>
                  <div className="flex gap-4 mb-4">
                    <Stat icon={Users} label="Клиентов" value={preview.clients.length} />
                    <Stat icon={CalendarDays} label="Записей" value={preview.appointments.length} />
                    <Stat icon={Wallet} label="Расходов" value={preview.expenses.length} />
                  </div>
                </CardContent>
              </Card>

              {preview.clients.length > 0 && (
                <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                  <CardContent className="p-5">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--app-text-secondary)' }}>
                      <Users size={14} /> Клиенты ({preview.clients.length})
                      {preview.clients.some(c => c._existing) && (
                        <Badge className="bg-amber-900/30 text-amber-400 border border-amber-900/50 text-[10px] ml-2">
                          {preview.clients.filter(c => c._existing).length} уже в базе
                        </Badge>
                      )}
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {preview.clients.map((c, i) => (
                        <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${c._existing ? 'border' : ''}`}
                          style={{ background: 'var(--app-card-deep)', borderColor: c._existing ? 'rgba(217,119,6,0.3)' : 'transparent' }}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm truncate" style={{ color: 'var(--app-text)' }}>{c.name}</p>
                              {c._existing && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-900/20 text-amber-500 shrink-0">
                                  совпадение: {c._existing_name}
                                </span>
                              )}
                            </div>
                            <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>{c.phone}{c.email && ` · ${c.email}`}</p>
                          </div>
                          {c._existing && (
                            <div className="flex items-center gap-1 shrink-0">
                              {[
                                { value: 'update', label: 'Обновить', cls: 'bg-amber-700/20 text-amber-400 border-amber-800/40' },
                                { value: 'create', label: 'Новый', cls: 'bg-sky-900/20 text-sky-400 border-sky-800/40' },
                                { value: 'skip', label: 'Пропустить', cls: 'bg-stone-800 text-stone-500 border-stone-700' },
                              ].map(opt => (
                                <button key={opt.value}
                                  onClick={() => {
                                    const updated = { ...preview };
                                    updated.clients = [...updated.clients];
                                    updated.clients[i] = { ...updated.clients[i], _action: opt.value };
                                    setPreview(updated);
                                  }}
                                  className={`text-[10px] px-2 py-1 rounded border transition-all ${
                                    c._action === opt.value ? opt.cls + ' font-medium' : 'bg-transparent text-stone-600 border-stone-800 hover:border-stone-600'
                                  }`}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          )}
                          {!c._existing && c.notes && <span className="text-[10px] shrink-0 max-w-[150px] truncate" style={{ color: 'var(--app-text-faint)' }}>{c.notes}</span>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {preview.appointments.length > 0 && (
                <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                  <CardContent className="p-5">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--app-text-secondary)' }}>
                      <CalendarDays size={14} /> Записи ({preview.appointments.length})
                    </h4>
                    <p className="text-xs mb-3" style={{ color: 'var(--app-text-muted)' }}>
                      Проверьте формат и длительность до импорта. Если формат не удалось определить, выберите его вручную.
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {preview.appointments.map((a, i) => (
                        <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg border"
                          style={{ background: 'var(--app-card-deep)', borderColor: a.format_id ? 'var(--app-border)' : 'rgba(217,119,6,0.55)' }}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm" style={{ color: 'var(--app-text)' }}>{a.client_name}</p>
                            <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
                              {a.date} {a.time}{a.duration ? ` · ${a.duration} мин.` : ''}
                            </p>
                          </div>
                          <Select value={a.format_id || ''} onValueChange={value => {
                            const selected = (preview.meeting_formats || []).find(item => item.id === value);
                            if (!selected) return;
                            setPreview(current => ({
                              ...current,
                              appointments: current.appointments.map((item, index) => index === i ? {
                                ...item,
                                format_id: selected.id,
                                format_name: selected.name,
                                duration: selected.duration,
                                _format_resolved: true,
                              } : item),
                            }));
                          }}>
                            <SelectTrigger className="sm:w-56 bg-stone-950/50 border-stone-800 text-stone-200 text-xs">
                              <SelectValue placeholder="Выберите формат" />
                            </SelectTrigger>
                            <SelectContent className="bg-stone-900 border-stone-800">
                              {(preview.meeting_formats || []).map(formatItem => (
                                <SelectItem key={formatItem.id} value={formatItem.id}>
                                  {formatItem.name} ({formatItem.duration} мин.)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {preview.expenses.length > 0 && (
                <Card style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                  <CardContent className="p-5">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--app-text-secondary)' }}>
                      <Wallet size={14} /> Расходы ({preview.expenses.length})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {preview.expenses.map((e, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'var(--app-card-deep)' }}>
                          <p className="text-sm" style={{ color: 'var(--app-text)' }}>{e.description || e.category}</p>
                          <p className="text-sm font-medium" style={{ color: 'var(--app-accent)' }}>{formatMoney(e.amount, currency)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex gap-3 pb-8">
                <Button onClick={reset} variant="outline" className="flex-1" style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-secondary)' }}>Отменить</Button>
                <Button onClick={confirmImport} disabled={importing || preview.appointments.some(a => !a.format_id)} className="flex-1 text-white" style={{ background: 'var(--app-accent)' }}>
                  {importing ? <><Loader2 size={14} className="mr-2 animate-spin" /> Импорт...</> : 'Импортировать всё'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="text-center">
      <Icon size={18} className="mx-auto mb-1" style={{ color: value > 0 ? 'var(--app-accent)' : 'var(--app-text-faint)' }} />
      <p className="text-lg font-bold" style={{ color: 'var(--app-text)' }}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--app-text-muted)' }}>{label}</p>
    </div>
  );
}
